import { useEffect } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import {
  setIsTableLoading,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
} from "src/redux/slices/TestQuestionSlice";
import { RootState } from "src/redux/store";
import { useDeleteTestQuestionMutation, useGetAllTestQuestionWithPaginationQuery } from "src/services/TestQuestionServices";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import TestQuestionListing from "./TestQuestionListing";
import { Chip, Tooltip } from "@mui/material";

const TestQuestionListingWrapper = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate()

  const [deleteTestQuestion] = useDeleteTestQuestionMutation();

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.testQuestion);

  const { data, isLoading, isFetching } = useGetAllTestQuestionWithPaginationQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: [
      "questionTitle"
    ],
    page: page,
    filterBy: [],
    questionCategory: [],
    questionTechnology: [],
    questionTag: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
      dispatch(setIsTableLoading(false));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (jobId: string) => {
    return [
      {
        label: (
          <div className="flex items-center gap-2 text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          navigate(`edit/${jobId}`);
        },
        actionName: UserModuleActionTypes.EDIT,
        moduleName: UserModuleNameTypes.TEST_QUESTION,
      },
      {
        label: (
          <div className="flex items-center gap-2 font-semibold text-red-600">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to Delete this Technology ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result?.isConfirmed) {
                deleteTestQuestion({ id: jobId }).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.DELETE,
        moduleName: UserModuleNameTypes.TEST_QUESTION,
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "questionType",
      headerName: "Question Type",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {(row?.questionType).replaceAll('_' , ' ')} </div>
      ),
    },

    {
      field: "questionTitle",
      headerName: "Title",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {row?.questionTitle} </div>
      ),
    },

    {
      field: "description",
      headerName: "Description",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120px] l	",
      renderCell: (row: any) => (
        <Tooltip title={row?.description}>
        <div className="capitalize min-w-[120px] line-clamp-2"> {row?.description} </div>
        </Tooltip>
      ),
    },

    {
      field: "questionTag",
      headerName: "Tag",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {row?.questionTag?.map((el: any) => {
          return (
            <Chip
              key={el?._id}
              label={el}
              className="me-2 my-1"
            />
          )
        })} </div>
      ),
    },

    {
      field: "action",
      headerName: "",
      flex: "flex-[1_1_0%]",
      extraClasses: " py-2",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];
  return (
    <div>
      <TestQuestionListing
        rows={items}
        columns={columns}
        getActionOptions={getActionOptions}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />
    </div>
  );
};

export default TestQuestionListingWrapper;
